import { useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, SearchInput } from '@ui';
import { ArrowLeft, Locate, MapPin, X } from '@ui/icons';
import { t } from '@shared/i18n';
import { cn } from '@shared/lib';
import { toast } from '@shared/lib/toast';
import {
  autocomplete,
  geolocateByIp,
  getCurrentPosition,
  nearbySearch,
  placeDetails,
  reverseGeocode,
  shiftLatLng,
  staticMapUrl,
  type GeolocationFailure,
  type LocationPlace,
  type LocationSuggestion,
} from '@entities/location';
import { InteractiveMap } from './InteractiveMap';

export interface LocationModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly value?: LocationPlace | null;
  readonly onSelect: (location: LocationPlace | null) => void;
}

type LocationCopy = typeof t.newBill.locationModal;

const SEARCH_DEBOUNCE_MS = 300;
const MAP_WIDTH = 500;
const MAP_HEIGHT = 400;
/** Default static-map zoom (street level) — the drag math converts pixels → degrees at the
 *  CURRENT zoom, which the +/− controls adjust within [MIN_ZOOM, MAX_ZOOM]. */
const MAP_ZOOM = 17;
const MIN_ZOOM = 3;
const MAX_ZOOM = 20;
/** Pointer movement below this (px) is a tap, not a drag — don't re-center for it. */
const DRAG_THRESHOLD_PX = 3;

/** "62 m" under 1 km, else "1.3 km" — matches the Flutter distance-label formatting. */
function formatDistance(distanceMeters: number): string {
  return distanceMeters >= 1000
    ? `${(distanceMeters / 1000).toFixed(1)} km`
    : `${Math.round(distanceMeters)} m`;
}

function geolocationErrorMessage(reason: GeolocationFailure, c: LocationCopy): string {
  if (reason === 'unsupported') return c.locationUnsupported;
  if (reason === 'permission_denied') return c.locationPermissionDenied;
  return c.locationUnavailable;
}

/** GPS position + its reverse-geocoded place. Does NOT toast — callers decide (the initial-open
 *  resolve suppresses it when a StrictMode-superseded run loses the race; the button never does).
 *  When the browser can't produce a fix at all (desktops without GPS commonly can't, even after
 *  the low-accuracy retry inside `getCurrentPosition`), an IP-based approximate position stands
 *  in — city-level, but enough to center the map and fill the nearby list. A permission DENIAL
 *  stays an error (mirrors the mobile abort): the user asked not to be located. */
async function resolveCurrentLocation(): Promise<
  | { ok: true; origin: { lat: number; long: number }; place: LocationPlace | null }
  | { ok: false; reason: GeolocationFailure }
> {
  const pos = await getCurrentPosition();
  let coords = pos.ok ? pos.value : null;
  if (!coords) {
    if (!pos.ok && pos.reason === 'permission_denied') return { ok: false, reason: pos.reason };
    coords = await geolocateByIp();
    if (!coords) return { ok: false, reason: 'unavailable' };
  }
  const place = await reverseGeocode(coords.lat, coords.long);
  return { ok: true, origin: { lat: coords.lat, long: coords.long }, place };
}

function LocationResultsList({
  isSearching,
  searchLoading,
  suggestions,
  resolvingPlaceId,
  onSelectSuggestion,
  nearbyLoading,
  nearby,
  selected,
  onSelectNearby,
  c,
}: {
  readonly isSearching: boolean;
  readonly searchLoading: boolean;
  readonly suggestions: readonly LocationSuggestion[];
  readonly resolvingPlaceId: string | null;
  readonly onSelectSuggestion: (suggestion: LocationSuggestion) => void;
  readonly nearbyLoading: boolean;
  readonly nearby: readonly LocationPlace[];
  readonly selected: LocationPlace | null;
  readonly onSelectNearby: (place: LocationPlace) => void;
  readonly c: LocationCopy;
}) {
  if (isSearching && searchLoading) {
    return <li className="py-8 text-center text-sm text-muted">{c.loading}</li>;
  }
  if (isSearching && suggestions.length === 0) {
    return (
      <li className="flex flex-col items-center gap-1 py-8 text-center">
        <span className="text-sm font-medium text-muted">{c.noResults}</span>
        <span className="text-xs text-muted-soft">{c.tryDifferentSearch}</span>
      </li>
    );
  }
  if (isSearching) {
    return (
      <>
        {suggestions.map((item) => {
          const isResolving = resolvingPlaceId === item.placeId;
          return (
            <li key={item.placeId}>
              <button
                type="button"
                disabled={isResolving}
                onClick={() => onSelectSuggestion(item)}
                className="flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors disabled:opacity-60"
              >
                <div className="flex flex-col items-center shrink-0 mt-0.5">
                  <MapPin className="size-5 text-muted-soft" />
                  {item.distanceMeters !== undefined && (
                    <span className="text-[9px] font-semibold text-muted-soft mt-0.5">
                      {formatDistance(item.distanceMeters)}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-fg-strong leading-tight">
                    {item.mainText}
                  </span>
                  {item.secondaryText && (
                    <span className="block text-xs text-muted mt-1 leading-normal wrap-break-word">
                      {item.secondaryText}
                    </span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </>
    );
  }
  if (nearbyLoading) {
    return <li className="py-8 text-center text-sm text-muted">{c.loading}</li>;
  }
  if (nearby.length === 0) {
    return (
      <li className="flex flex-col items-center gap-1 py-8 text-center">
        <span className="text-sm font-medium text-muted">{c.noResults}</span>
      </li>
    );
  }
  return (
    <>
      {nearby.map((item) => {
        const isItemChosen = selected?.lat === item.lat && selected.long === item.long;
        return (
          <li key={`${item.lat},${item.long}`}>
            <button
              type="button"
              onClick={() => onSelectNearby(item)}
              className={`flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors ${
                isItemChosen ? 'bg-primary/5 border-l-[3px] border-primary pl-[17px]' : ''
              }`}
            >
              <div className="flex flex-col items-center shrink-0 mt-0.5">
                <MapPin className={`size-5 ${isItemChosen ? 'text-primary' : 'text-muted-soft'}`} />
                {item.distanceMeters !== undefined && (
                  <span className="text-[9px] font-semibold text-muted-soft mt-0.5">
                    {formatDistance(item.distanceMeters)}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-fg-strong leading-tight">
                  {item.name}
                </span>
                <span className="block text-xs text-muted mt-1 leading-normal wrap-break-word">
                  {item.formattedAddress}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </>
  );
}

function LocationBody({
  value,
  onSelect,
  onCancel,
}: {
  readonly value: LocationPlace | null | undefined;
  readonly onSelect: (location: LocationPlace | null) => void;
  readonly onCancel: () => void;
}) {
  const c = t.newBill.locationModal;

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<LocationPlace | null>(value ?? null);
  const [origin, setOrigin] = useState<{ lat: number; long: number } | null>(
    value ? { lat: value.lat, long: value.long } : null,
  );
  const [originLoading, setOriginLoading] = useState(!value);

  const [nearby, setNearby] = useState<LocationPlace[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);

  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [resolvingPlaceId, setResolvingPlaceId] = useState<string | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTokenRef = useRef(0);

  // Shared by the initial resolve and the "use current location" button — both just want origin +
  // loading state updated from a GPS fetch. Wrapped in useCallback (not inlined in the effect body)
  // so its setState calls happen inside a called function, not the effect's own closure. Doesn't
  // toast on failure — the caller decides (the initial resolve suppresses it when superseded).
  const resolveOrigin = useCallback(async () => {
    setOriginLoading(true);
    const result = await resolveCurrentLocation();
    setOriginLoading(false);
    if (result.ok) setOrigin(result.origin);
    return result;
  }, []);

  // Resolve the starting point once on open: an existing selection's coords, else the device GPS.
  // `cancelled` guards against StrictMode's dev-only double-invoke (mount → cleanup → mount) firing
  // a second GPS request and showing the error toast twice for what's really one logical attempt.
  useEffect(() => {
    if (origin) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot GPS fetch on open, mirrors ReceiptUploadModal's object-URL effect
    void resolveOrigin().then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        toast.error(geolocationErrorMessage(result.reason, c));
        return;
      }
      if (result.place) setSelected((current) => current ?? result.place);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per modal open (Body remounts)
  }, []);

  const fetchNearby = useCallback(async (lat: number, long: number) => {
    setNearbyLoading(true);
    const results = await nearbySearch(lat, long);
    setNearby(results);
    setNearbyLoading(false);
  }, []);

  // Nearby list — fetched once the origin is known.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch triggered by a resolved origin, not derivable from render
    if (origin) void fetchNearby(origin.lat, origin.long);
  }, [origin, fetchNearby]);

  const runSearch = useCallback(
    async (q: string, bias: { lat: number; long: number } | undefined, token: number) => {
      setSearchLoading(true);
      const results = await autocomplete(q, bias);
      if (searchTokenRef.current === token) {
        setSuggestions(results);
        setSearchLoading(false);
      }
    },
    [],
  );

  const clearSearch = useCallback(() => {
    setSuggestions([]);
    setSearchLoading(false);
  }, []);

  // Debounced autocomplete as the user types.
  useEffect(() => {
    if (searchDebounceRef.current !== null) clearTimeout(searchDebounceRef.current);
    const q = query.trim();
    if (q === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears stale results the instant the box empties
      clearSearch();
      return;
    }
    const token = ++searchTokenRef.current;
    searchDebounceRef.current = setTimeout(
      () => void runSearch(q, origin ?? undefined, token),
      SEARCH_DEBOUNCE_MS,
    );
    return () => {
      if (searchDebounceRef.current !== null) clearTimeout(searchDebounceRef.current);
    };
  }, [query, origin, runSearch, clearSearch]);

  const isSearching = query.trim().length > 0;

  // ---- Map drag (mobile parity: fixed center pin, the map pans underneath) ----
  // `dragCenter` is the last dragged-to center — it wins over the selection for the map view
  // until an explicit pick (suggestion / nearby / current location) re-centers on that pick.
  const [dragCenter, setDragCenter] = useState<{ lat: number; long: number } | null>(null);
  // Zoom level for the map view — adjustable via the +/− controls; drag math follows it.
  const [mapZoom, setMapZoom] = useState(MAP_ZOOM);
  // Live pixel offset while a drag is in progress — applied as a CSS translate on the image.
  const [dragOffset, setDragOffset] = useState<{ dx: number; dy: number } | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const mapPanelRef = useRef<HTMLDivElement>(null);
  const dragGeocodeTokenRef = useRef(0);
  // Prefer the REAL Google map (tiles stream while panning — smooth, never blank). Flipped to
  // false when the Maps JS API can't load (no browser key, blocked script, API not enabled on the
  // key), which falls back to the draggable Static Maps image below — the previous behaviour.
  const [interactiveMap, setInteractiveMap] = useState(true);

  const handleSelectSuggestion = (suggestion: LocationSuggestion) => {
    setResolvingPlaceId(suggestion.placeId);
    void placeDetails(suggestion.placeId).then((place) => {
      setResolvingPlaceId(null);
      if (place) {
        setDragCenter(null); // an explicit pick re-centers the map on it
        setSelected(place);
      }
    });
  };

  const handleUseCurrentLocation = (e: React.MouseEvent) => {
    e.stopPropagation();
    void resolveOrigin().then((result) => {
      if (!result.ok) {
        toast.error(geolocationErrorMessage(result.reason, c));
        return;
      }
      // Center the map on the fresh position UNCONDITIONALLY — the map must jump to where the
      // user is even when the address lookup for that point failed (place null); previously a
      // prior selection/drag kept winning the center and the map appeared to ignore the click.
      setDragCenter({ lat: result.origin.lat, long: result.origin.long });
      if (result.place) setSelected(result.place);
    });
  };

  const handleSelectNearby = (place: LocationPlace) => {
    setDragCenter(null);
    setSelected(place);
  };

  const handleConfirm = () => onSelect(selected);

  const mapCenter = dragCenter ?? (selected ? { lat: selected.lat, long: selected.long } : origin);
  const mapSrc = mapCenter
    ? staticMapUrl(mapCenter.lat, mapCenter.long, {
        width: MAP_WIDTH,
        height: MAP_HEIGHT,
        zoom: mapZoom,
        marker: false, // the fixed pin overlay below IS the marker — the map pans under it
      })
    : null; // null only when no GPS fix yet — map shows placeholder until origin is resolved

  const zoomBy = (delta: number) =>
    setMapZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta)));

  /** Re-center + reverse-geocode the pinned point (mobile's `onCameraIdle`). Shared by the
   *  interactive map's idle event and the static map's drag-release, so both paths select the
   *  same way. A stale-response guard keeps rapid consecutive moves consistent. */
  const commitMapCenter = useCallback((next: { lat: number; long: number }) => {
    setDragCenter(next);
    const token = ++dragGeocodeTokenRef.current;
    void reverseGeocode(next.lat, next.long).then((place) => {
      if (place && dragGeocodeTokenRef.current === token) setSelected(place);
    });
  }, []);

  const handleMapIdle = useCallback(
    (next: { lat: number; long: number }, nextZoom: number) => {
      setMapZoom(nextZoom);
      commitMapCenter(next);
    },
    [commitMapCenter],
  );

  const handleMapUnavailable = useCallback(() => setInteractiveMap(false), []);
  /** The static image (+ its manual drag math) is the fallback — used until/unless the real map
   *  is available. `mapCenter` null means no fix yet: the placeholder shows either way. */
  const useStaticMap = !interactiveMap;

  const handleMapPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!mapCenter) return;
    // Let the "Use current location" overlay button receive its own clicks untouched.
    if (e.target instanceof Element && e.target.closest('button')) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handleMapPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (e.pointerId !== start?.pointerId) return;
    setDragOffset({ dx: e.clientX - start.x, dy: e.clientY - start.y });
  };
  const handleMapPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (e.pointerId !== start?.pointerId) return;
    dragStartRef.current = null;
    setDragOffset(null);
    if (!mapCenter) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
    // Screen px → map px: the image (MAP_WIDTH×MAP_HEIGHT logical px) is object-cover scaled to
    // the panel, so divide by the cover factor; dragging the image by +d moves the CENTER by −d.
    const panel = mapPanelRef.current;
    const cover = panel
      ? Math.max(panel.clientWidth / MAP_WIDTH, panel.clientHeight / MAP_HEIGHT)
      : 1;
    const next = shiftLatLng(mapCenter.lat, mapCenter.long, -dx / cover, -dy / cover, mapZoom);
    // The pinned center becomes the selection, reverse-geocoded for its address.
    commitMapCenter(next);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Search Input Area */}
      <div className="border-b border-border bg-surface px-5 py-4">
        <SearchInput
          aria-label={c.searchPlaceholder}
          placeholder={c.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
          className="rounded-lg"
        />
      </div>

      {/* Split Panels */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
        {/* Left List Panel */}
        <div className="flex flex-col w-full md:w-[360px] shrink-0 border-b md:border-b-0 md:border-r border-border min-h-0 bg-surface">
          <div className="px-5 py-3 border-b border-border bg-slate-50/50">
            <span className="text-[11px] font-bold tracking-wider text-muted-soft uppercase">
              {isSearching ? c.searchResults : c.nearbyLocations}
            </span>
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto divide-y divide-border">
            <LocationResultsList
              isSearching={isSearching}
              searchLoading={searchLoading}
              suggestions={suggestions}
              resolvingPlaceId={resolvingPlaceId}
              onSelectSuggestion={handleSelectSuggestion}
              nearbyLoading={nearbyLoading}
              nearby={nearby}
              selected={selected}
              onSelectNearby={handleSelectNearby}
              c={c}
            />
          </ul>
        </div>

        {/* Right Map Panel — a DRAGGABLE Static Maps view: a fixed center pin with the map
            panning underneath (mobile parity); releasing re-centers + reverse-geocodes. */}
        <div
          ref={mapPanelRef}
          // The drag handlers belong to the STATIC fallback only — the interactive map does its
          // own (smooth, tile-streaming) panning and must receive the pointer events itself.
          {...(useStaticMap
            ? {
                onPointerDown: handleMapPointerDown,
                onPointerMove: handleMapPointerMove,
                onPointerUp: handleMapPointerEnd,
                onPointerCancel: handleMapPointerEnd,
              }
            : {})}
          className={cn(
            'relative flex-1 bg-slate-100 min-h-0 h-[240px] md:h-auto overflow-hidden select-none',
            useStaticMap && 'touch-none',
            useStaticMap && mapSrc && (dragOffset ? 'cursor-grabbing' : 'cursor-grab'),
          )}
        >
          {mapCenter && !useStaticMap ? (
            <>
              <InteractiveMap
                center={mapCenter}
                zoom={mapZoom}
                onIdle={handleMapIdle}
                onUnavailable={handleMapUnavailable}
                ariaLabel={c.mapPreviewAlt}
                className="h-full w-full"
              />
              {/* The FIXED center pin — its tip marks the point being selected. */}
              <span
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-full"
              >
                <MapPin className="size-8 text-primary drop-shadow-md" />
              </span>
            </>
          ) : mapSrc ? (
            <>
              <img
                src={mapSrc}
                alt={c.mapPreviewAlt}
                draggable={false}
                className="h-full w-full object-cover"
                style={
                  dragOffset
                    ? { transform: `translate(${dragOffset.dx}px, ${dragOffset.dy}px)` }
                    : undefined
                }
                width={MAP_WIDTH}
                height={MAP_HEIGHT}
              />
              {/* The FIXED center pin — its tip marks the point being selected. */}
              <span
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-full"
              >
                <MapPin className="size-8 text-primary drop-shadow-md" />
              </span>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-muted">
              {originLoading ? c.loading : c.mapNoPreview}
            </div>
          )}

          {/* Floating Use Current Location Button overlay */}
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={originLoading}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex h-9 items-center gap-1.5 rounded-full bg-white px-4 text-xs font-semibold text-primary shadow-lg border border-border hover:bg-slate-50 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60"
          >
            <Locate className="size-3.5 shrink-0" />
            {c.useCurrentLocation}
          </button>

          {/* Zoom controls — standard +/− stack; the drag math follows the current zoom, so any
              place is reachable by zooming out, panning, and zooming back in. */}
          {mapCenter ? (
            <div className="absolute bottom-4 right-4 z-30 flex flex-col overflow-hidden rounded-lg border border-border bg-white shadow-lg">
              <button
                type="button"
                onClick={() => zoomBy(1)}
                disabled={mapZoom >= MAX_ZOOM}
                aria-label={c.zoomIn}
                className="flex size-9 items-center justify-center text-lg font-semibold text-fg hover:bg-slate-50 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                +
              </button>
              <span aria-hidden className="h-px bg-border" />
              <button
                type="button"
                onClick={() => zoomBy(-1)}
                disabled={mapZoom <= MIN_ZOOM}
                aria-label={c.zoomOut}
                className="flex size-9 items-center justify-center text-lg font-semibold text-fg hover:bg-slate-50 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                −
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer Area */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center border-t border-border bg-slate-50/70 p-5">
        {/* Selected Location Details Card */}
        <div className="flex items-start gap-3 bg-white border border-border rounded-lg p-3 min-w-0 flex-1 md:max-w-[480px]">
          <div className="flex items-center justify-center size-9 rounded-md bg-primary/10 text-primary shrink-0 mt-0.5">
            <MapPin className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block text-[11px] font-bold tracking-wider text-muted-soft uppercase leading-none mb-1">
              {c.selectedLocation}
            </span>
            {selected ? (
              <>
                <span className="block text-sm font-semibold text-fg-strong leading-snug truncate">
                  {selected.name}
                </span>
                <span className="block text-xs text-muted leading-normal truncate mt-0.5">
                  {selected.formattedAddress}
                </span>
              </>
            ) : (
              <span className="block text-sm text-muted italic">{c.noneSelected}</span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2.5 justify-end">
          <Button variant="secondary" onClick={onCancel}>
            {c.cancel}
          </Button>
          <Button disabled={!selected} onClick={handleConfirm}>
            {c.confirmLocation}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function LocationModal({ open, onOpenChange, value, onSelect }: LocationModalProps) {
  const c = t.newBill.locationModal;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50 backdrop-blur-[1px]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-60 flex h-[620px] max-h-[90vh] w-[calc(100vw-2rem)] max-w-[860px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2.5">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted hover:text-fg hover:bg-border-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={c.cancel}
            >
              <ArrowLeft className="size-5" />
            </button>
            <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
              {c.title}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t.newBill.close}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          {/* Body content — remounts each open so search/selection state resets */}
          {open && (
            <LocationBody
              value={value}
              onSelect={(location) => {
                onSelect(location);
                onOpenChange(false);
              }}
              onCancel={() => onOpenChange(false)}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
